import { isPlatformBrowser, ViewportScroller } from '@angular/common';
import { provideHttpClient } from '@angular/common/http';
import {
  ApplicationConfig,
  ApplicationRef,
  DOCUMENT,
  PLATFORM_ID,
  inject,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  provideClientHydration,
  withEventReplay,
} from '@angular/platform-browser';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withPreloading,
  withRouterConfig,
  withViewTransitions,
} from '@angular/router';
import { APP_ROUTES } from '../app.routing';
import { ThemeService } from '../components/theme/theme.service';
import { IdlePreload } from '../custom.preloading-strategy';
import { LeadSourceService } from '../lead-source.service';

/** Mirrors --fu-anchor-offset (styles.scss), the scroll-margin-top of every
 * anchor, so the router's anchor scrolls and the browser's native fragment
 * scroll land on the same pixel. */
const ANCHOR_OFFSET_PX = 96;

export const APP_BASE_CONFIG: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    // Incremental hydration is on by default from v22, and `FetchBackend` is the
    // default `HttpBackend`, so `withIncrementalHydration()` / `withFetch()` were
    // deprecated no-ops. `withInterceptorsFromDi()` went with them: nothing in
    // the app or its dependencies registers an `HTTP_INTERCEPTORS` provider.
    provideClientHydration(withEventReplay()),
    provideHttpClient(),
    provideRouter(
      APP_ROUTES,
      withComponentInputBinding(),
      withPreloading(IdlePreload),
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      }),
      withRouterConfig({
        onSameUrlNavigation: 'reload',
      }),
      withViewTransitions({ skipInitialTransition: true }),
    ),
    provideAppInitializer(() => {
      const viewportScroller = inject(ViewportScroller);
      viewportScroller.setOffset([0, ANCHOR_OFFSET_PX]);

      if (!isPlatformBrowser(inject(PLATFORM_ID))) {
        return;
      }
      // With hydration enabled the router drops the initial navigation's
      // anchor scroll outright (RouterScroller skips its Scroll event while
      // hydrating), and the browser's own fragment scroll at `load` is
      // clamped back to the top when hydration tears the SSR DOM down
      // around the CMS queries. One re-assert once the app reports stable
      // is the whole fix; the router itself handles every later in-app
      // fragment navigation. History: changelog entry 56.
      //
      // The hash is read there and not at boot, because a fragment can also
      // arrive during hydration: `/contact?prefill=` seeds the form and then
      // navigates to `#contact-form` (`SiteComponent`), landing inside the
      // same window where the router's own anchor scroll is dropped.
      const doc = inject(DOCUMENT);
      const appRef = inject(ApplicationRef);

      void appRef.whenStable().then(() => {
        const fragment = doc.location.hash.slice(1);

        if (fragment) {
          viewportScroller.scrollToAnchor(fragment);
        }
      });
    }),
    provideAppInitializer(() => {
      // Construct ThemeService at startup so the persisted/system theme
      // reconciles onto <html> before first render; the pre-paint script in
      // index.html only forces theme-light.
      inject(ThemeService);
      // Reads the entry URL and referrer now, before the router moves on and
      // a later `?prefill` redirect rewrites the URL. `EmailService` is first
      // constructed by whichever form the visitor reaches, pages later.
      inject(LeadSourceService);
    }),
  ],
};
